import { observer } from 'mobx-react';
import { FC, Fragment, SyntheticEvent, useEffect, useMemo } from 'react';

import { CampaignsStore } from '@servicetitan/application-data/campaigns';
import { FeaturesStore } from '@servicetitan/application-data/features';
import {
    CallType,
    Carrier,
    DialpadNumberAssignmentTargetState,
    DialpadNumberTargetType,
    PhoneNumberModel,
} from '@servicetitan/application-data/telecom';
import {
    AnvilSelectOptionsProps,
    Avatar,
    Banner,
    BodyText,
    Button,
    ButtonToggle,
    DropdownProps,
    Form,
    Link,
    Modal,
    ProgressBar,
    Stack,
    Tag,
    Text,
} from '@servicetitan/design-system';
import { Label } from '@servicetitan/form';
import { useFormatPhone } from '@servicetitan/react-hooks';
import { useDependencies } from '@servicetitan/react-ioc';

import { PhoneNumbersStore } from '../../stores/phone-numbers.store';
import { ForwardingNumberInput } from './forwarding-number-input';

export const NumberEdit: FC<{
    id: string | undefined;
    hideSetCampaign?: boolean;
    allowIncomingSms?: boolean;
    addingCallExtension?: boolean;
    onSave?(model: PhoneNumberModel): void;
}> = observer(props => {
    const [store, { campaigns }, { features }] = useDependencies(
        PhoneNumbersStore,
        CampaignsStore,
        FeaturesStore
    );
    const formatPhone = useFormatPhone();
    const isNew = !props.id || !props.id.length;
    const numberFormState = isNew
        ? store.formState.$.editFormState
        : store.phoneNumbersGridState.inEdit.get(props.id!)!.form;

    useEffect(() => {
        if (isNew && props.allowIncomingSms !== undefined) {
            store.formState.$.editFormState.$.enableSms.onChange(props.allowIncomingSms);
        }
    }, [isNew, props.allowIncomingSms, store]);

    const {
        forwardTo,
        friendlyName,
        useDefaultCallForwarding,
        enableSms,
        campaignId,
        defaultCallType,
        alias,
        isDirectDial,
        localUserId,
        isDniAllocated,
        assignmentTarget,
        carrier,
        tcrCampaignId,
        tcrCampaignExternalId,
    } = numberFormState.$;
    const { isLocal } = store.formState.$.searchFormState.$;
    const shouldDetachTcrCampaignFromPhone = store.shouldDetachTcrCampaignFromPhone;

    const onEnableSmsChange = (value: boolean | undefined) => enableSms.onChange(value!);
    const onDefaultCallTypeChange = (value: boolean | undefined) =>
        defaultCallType.onChange(value ? CallType.NotLead : undefined);
    const onCampaignChange = (_0: SyntheticEvent<HTMLElement>, data: DropdownProps) =>
        campaignId.onChange(data.value as number | undefined);
    const onAliasChange = (_0: SyntheticEvent<HTMLInputElement, Event>, data: { value: string }) =>
        alias.onChange(data.value);
    const onIsDirectDialChange = (value: string | undefined) => {
        const val = value === 'true';
        if (!val && localUserId.value) {
            localUserId.onChange(undefined);
        }
        isDirectDial.onChange(val);
    };
    const onUserChange = (_0: SyntheticEvent<HTMLElement>, data: DropdownProps) =>
        localUserId.onChange(data.value as number | undefined);
    const handleSave = async () => {
        const model = await store.saveNumber(numberFormState!, !props.id);
        if (props.onSave && model) {
            props.onSave(model);
        }
    };

    const realCampaigns = useMemo(
        () =>
            campaigns.map(x => {
                return { key: x.id, value: x.id, text: x.name };
            }),
        [campaigns]
    );
    const users = useMemo(() => {
        return store.users!.map(x => {
            return {
                key: x.serviceTitanUserId,
                text: x.name,
                value: x.serviceTitanUserId,
                content: (
                    <Stack alignItems="center" spacing={2}>
                        <Avatar name={x.name} size="m" autoColor />
                        <Text el="span" size={3}>
                            {x.name}
                        </Text>
                        <Tag>{x.role}</Tag>
                    </Stack>
                ),
            };
        });
    }, [store.users]);

    const onTargetChangeHandler = (data: AnvilSelectOptionsProps) =>
        assignmentTarget.onChange(
            store.targets!.find(x => x.id === data?.value) || {
                type: DialpadNumberTargetType.Unknown,
                name: 'Reserve Pool',
                id: 0,
                state: DialpadNumberAssignmentTargetState.Unknown,
            }
        );

    const targets = useMemo(() => {
        return [
            ...Object.entries(
                (!assignmentTarget.$?.id || store.targets.find(x => x.id === assignmentTarget.$?.id)
                    ? store.targets
                    : [...store.targets, assignmentTarget.$])!
                    .filter(x =>
                        x.name
                            ?.toLowerCase()
                            ?.includes(store.numberAssignmentSearchTerm?.toLowerCase())
                    )
                    .reduce((groupedOptions: any, option) => {
                        if (!groupedOptions[option.type]) {
                            groupedOptions[option.type] = [];
                        }
                        groupedOptions[option.type].push({
                            text: option.name,
                            value: option.id,
                        });
                        return groupedOptions;
                    }, {})
            ).map(
                ([type, options]) =>
                    ({
                        text: type,
                        options,
                    }) as AnvilSelectOptionsProps
            ),
            {
                options: [
                    {
                        text: 'Reserve Pool',
                        value: 0,
                    },
                ],
            },
        ];
    }, [assignmentTarget.$, store.targets, store.numberAssignmentSearchTerm]);

    return (
        <Fragment>
            <Modal.Header
                onClose={store.closeModal}
                title={`${isNew ? 'Add' : 'Edit'} ${formatPhone(friendlyName.value || '')}`}
            />
            <Modal.Content>
                {store.addEditNumberError && (
                    <Banner
                        icon
                        status="critical"
                        className="m-b-2"
                        title="Unable to process request."
                    >
                        <Text>{store.addEditNumberError}</Text>
                    </Banner>
                )}
                <Form>
                    <ForwardingNumberInput
                        forwardingNumber={forwardTo}
                        isDefault={useDefaultCallForwarding}
                    />
                    {props.allowIncomingSms === undefined && (
                        <Fragment>
                            <Text bold size={2} className="m-t-0-i m-b-1-i">
                                Allow Incoming SMS?
                            </Text>
                            <ButtonToggle
                                className="m-b-2"
                                onChange={onEnableSmsChange}
                                options={[
                                    {
                                        text: 'Yes',
                                        value: true,
                                    },
                                    {
                                        text: 'No',
                                        value: false,
                                    },
                                ]}
                                value={enableSms.value}
                            />
                        </Fragment>
                    )}
                    {!props.hideSetCampaign &&
                        (isDniAllocated.value ? (
                            <Form.Select
                                disabled
                                large
                                placeholder="Allocated to DNI"
                                className="m-b-2-i"
                                label={
                                    <Text el="span" className="m-b-1" size={2} bold>
                                        Set Campaign
                                    </Text>
                                }
                                options={[]}
                                data-cy="number-edit-campaign-allocated-to-DNI"
                            />
                        ) : (
                            <Form.Select
                                large
                                search
                                upward
                                clearable
                                className="m-b-2-i"
                                label={
                                    <Text el="span" className="m-b-1" size={2} bold>
                                        Set Campaign
                                    </Text>
                                }
                                value={campaignId.value}
                                options={realCampaigns}
                                onChange={onCampaignChange}
                                data-cy="number-edit-set-campaign"
                            />
                        ))}
                    {isDniAllocated.value && (
                        <Banner icon status="info" title="">
                            <BodyText el="span">
                                {`This number is currently allocated to DNI. To assign this number to a
                            different campaign, edit your `}
                                <Link
                                    href="#/Settings/MarketingIntegrations/Dni"
                                    target="_blank"
                                    primary
                                >
                                    DNI configuration
                                </Link>
                                {` and remove it from the selected numbers.`}
                            </BodyText>
                        </Banner>
                    )}
                    {features.EnableDefaultCallType && (
                        <Fragment>
                            <Text bold size={2} className="m-t-0-i m-b-1-i">
                                Default Call Type
                            </Text>
                            <ButtonToggle
                                className="m-b-2"
                                onChange={onDefaultCallTypeChange}
                                options={[
                                    {
                                        text: 'Lead',
                                        value: false,
                                    },
                                    {
                                        text: 'Not Lead',
                                        value: true,
                                    },
                                ]}
                                value={defaultCallType.value === CallType.NotLead}
                            />
                        </Fragment>
                    )}
                    {features.EnableCustomOutboundCallerId && (
                        <Form.Input
                            large
                            className="m-b-2-i"
                            label="Outbound Caller ID"
                            value={alias.value}
                            onChange={onAliasChange}
                        />
                    )}
                    {(isNew || !isDirectDial.value) &&
                        !props.addingCallExtension &&
                        store.isDialpadEnabled &&
                        carrier.value !== Carrier.Twilio && (
                            <Fragment>
                                {isNew && (
                                    <Fragment>
                                        <Text bold size={2} className="m-t-0-i m-b-1-i">
                                            Select Number Type
                                        </Text>
                                        <ButtonToggle
                                            className="m-b-2-i"
                                            onChange={onIsDirectDialChange}
                                            options={[
                                                {
                                                    text: 'Tracking',
                                                    value: 'false',
                                                },
                                                {
                                                    text: 'Direct Dial',
                                                    value: 'true',
                                                    disabled: users.length === 0 || !isLocal.value,
                                                },
                                            ]}
                                            value={
                                                isDirectDial.value === undefined
                                                    ? undefined
                                                    : String(isDirectDial.value)
                                            }
                                        />
                                    </Fragment>
                                )}
                                {isDirectDial.value ? (
                                    <Form.Select
                                        large
                                        search
                                        upward
                                        clearable
                                        className="m-b-2-i"
                                        label={
                                            <Fragment>
                                                <Text el="span" className="m-b-1" size={2} bold>
                                                    Select User
                                                </Text>
                                                <Label
                                                    label=""
                                                    hasError={localUserId.hasError}
                                                    error={localUserId.error}
                                                />
                                            </Fragment>
                                        }
                                        value={localUserId.value}
                                        options={users}
                                        onChange={onUserChange}
                                        error={localUserId.hasError}
                                    />
                                ) : store.numberAssignmentLoadingInProgress ? (
                                    <ProgressBar blue indeterminate className="m-t-5 m-b-2" />
                                ) : (
                                    <Form.AnvilSelect
                                        search={{
                                            value: store.numberAssignmentSearchTerm,
                                            onChange: store.handleSearch,
                                        }}
                                        trigger={{
                                            size: 'large',
                                            clearable: false,
                                        }}
                                        scrollHeight="300px"
                                        autoFlipVertically
                                        className="m-b-2-i"
                                        label={
                                            <Fragment>
                                                <Text el="span" className="m-b-1" size={2} bold>
                                                    Assign Number to Target
                                                </Text>
                                                <Label
                                                    label=""
                                                    hasError={assignmentTarget.hasError}
                                                    error={assignmentTarget.error}
                                                />
                                            </Fragment>
                                        }
                                        value={{
                                            value: assignmentTarget.value?.id || 0,
                                            text: assignmentTarget.value?.name || 'Reserve Pool',
                                        }}
                                        options={targets}
                                        onChange={onTargetChangeHandler}
                                        error={assignmentTarget.hasError}
                                    />
                                )}
                            </Fragment>
                        )}
                    {features.EnableTcrCampaignDissassociation && tcrCampaignExternalId.value && (
                        <Fragment>
                            <BodyText bold className="m-t-1 m-b-1" size="small">
                                TCR Campaign ID
                            </BodyText>
                            <BodyText className="m-b-1">{tcrCampaignExternalId.value}</BodyText>
                            {!shouldDetachTcrCampaignFromPhone && (
                                <Button
                                    className="m-b-1"
                                    onClick={() => (store.shouldDetachTcrCampaignFromPhone = true)}
                                >
                                    Disassociate
                                </Button>
                            )}
                            {shouldDetachTcrCampaignFromPhone && (
                                <Fragment>
                                    <BodyText className="m-t-2 m-b-1 c-red-600">
                                        This will be disassociated on clicking "Save"
                                    </BodyText>
                                    <Button
                                        className="m-b-1"
                                        onClick={() =>
                                            (store.shouldDetachTcrCampaignFromPhone = false)
                                        }
                                    >
                                        Undo
                                    </Button>
                                </Fragment>
                            )}
                        </Fragment>
                    )}
                </Form>
            </Modal.Content>
            <Modal.Footer align={isNew ? 'space-between' : 'right'}>
                {isNew && (
                    <Button onClick={store.goBack} disabled={store.saveInProgress}>
                        Back
                    </Button>
                )}
                <Button primary onClick={handleSave} disabled={store.saveInProgress}>
                    {isNew ? 'Add Number' : 'Save'}
                </Button>
            </Modal.Footer>
        </Fragment>
    );
});
